// vue
import { ref, reactive, computed, watch } from 'vue';

// store
import { useGl } from '@store/ts/gl';
import { useRefs } from '@store/ts/refs';
import { useStats } from '@store/stats';

// i18n
import { useI18n } from 'vue-i18n';

// services
import StatsService from '@services/statsService';
import WalletsService from '@services/walletsService';

// shared
import { CRUD_PAGER_FACTORY } from '@shared/factories';

// router
import { useRoute } from 'vue-router';

// UI
import { useNotification } from 'naive-ui';

export default function (props, context) {
    // store
    const gl = useGl();
    const refs = useRefs();
    const stats = useStats();

    // naive-ui
    const notification = useNotification();

    // i18n
    const { t } = useI18n();

    // router
    const route = useRoute();

    // vars
    const show = ref(false);
    const orders = ref([]);
    const checkBalanceLoading = ref(false);
    const addInsuranceOrderLoading = ref(false);
    const orderExecution = ref('limit');
    const calculateShow = ref(false);
    const balancesRefs = ref();
    const model = reactive({
        market: computed(() => orderExecution.value === 'market'),
        amount: null,
        rate: null,
    });
    watch(show, v => {
        if (!v) {
            model.amount = null;
            model.rate = null;
            balancesRefs.value = null;
            orders.value = [];
        }
    });

    const ordersFilters = computed(() => CRUD_PAGER_FACTORY({
        bots: [props.botShortInfo?.id],
        cycles: [props.activeCycle?.number_id],
    }, {
        perPage: 50,
    }));

    const leverage = computed(() => props.botShortInfo?.leverage);
    const isLong = computed(() => props.botShortInfo?.algo == 'long');
    const isFutures = computed(() => props.botShortInfo?.futures != 0);
    const r1 = computed(() => props.activeCycle?.r1);
    const r2 = computed(() => props.activeCycle?.r2);
    const takeprofitShow = computed(() => {
        return takeprofit.value && !isNaN(+model.amount) && +model.amount > 0
        && (orderExecution.value == 'limit' ? !isNaN(+model.rate)  && +model.rate > 0 : true);
    });
    const coin = computed(() => {
        return !props.botShortInfo.futures
            ? props.botShortInfo.tickers_for.split('/')[isLong.value ? 1 : 0]
            : props.botShortInfo.tickers_for.split('/')[props.botShortInfo.extra_data?.coin === 1 ? 0 : 1];
    });

    const orderExecutionTypes = computed(() => stats.orderExecutionTypes.map(el => ({
        label: el.title,
        value: el.id,
        ...el,
    })));

    const priceToZero = computed(() => {
        const a2 = Number(orderExecution.value == 'limit' ? model.rate : props.botShortInfo.buy);
        const p2 = Number(model.amount) * (isFutures.value ? leverage.value : 1);
        const a1 = Number(averagePrice.value);
        const p1 = !isLong.value && !isFutures.value
            ? Number(buyedVolume.value)
            : Number(buyedPriceAll.value);

        // console.log('Расчёт E');
        // console.log('Алго: ', props.botShortInfo?.algo);
        // console.log('Плечо: ', props.botShortInfo?.leverage);
        // console.log('A1: ', a1);
        // console.log('P1: ', p1);
        // console.log('A2: ', a2);
        // console.log('P2: ', p2);
        // console.log('Формула: ((a1 * p1) + (a2 * p2)) / (p1 + p2))');
        // console.log('=======================================');

        return ((a1 * p1) + (a2 * p2)) / (p1 + p2);
    });

    const takeprofit = computed(() => {
        const e = priceToZero.value;

        // console.log('Расчёт Y');
        // console.log('Алго: ', props.botShortInfo?.algo);
        // console.log('Плечо: ', props.botShortInfo?.leverage);
        // console.log('E: ', e);
        // console.log('R1: ', r1.value);
        // console.log('R2: ', r2.value);
        // console.log('=======================================');

        const result = isLong.value
            ? e * (1 + ((r2.value - r1.value) /r1.value ))
            : e * r2.value / r1.value;

        return parseFloat(result.toFixed(8));
    });

    const symbolBalance = computed(() => balancesRefs.value?.[coin.value] || '');
    const buyedVolume = computed(() => {
        const list =
            orders.value
                .filter(({ status, order_type }) => status == 1 && order_type == (isLong.value ? 'buy' : 'sell'))
                || [];

        const result = list.reduce((accum, value) => accum + +value.amount_processed, 0);

        return parseFloat(result.toFixed(6));
    });
    const buyedPriceAll = computed(() => {
        const list =
          orders.value
              .filter(({ status, order_type }) => status == 1 && order_type == (isLong.value ? 'buy' : 'sell'))
              || [];

        const result = list.reduce((accum, value) => accum + (value.rate * value.amount_processed), 0);

        return parseFloat(result.toFixed(6));
    });
    const averagePrice = computed(() => {
        return parseFloat((buyedPriceAll.value / buyedVolume.value).toFixed(6));
    });

    const onInsuranceOrder = async () => {
        if (!props.botShortInfo?.api_id) return;

        if(props.botShortInfo?.status != 1) {
            return void notification['error']({
                content: stats.localization['bot_not_running'],
                duration: 2500,
                keepAliveOnHover: true,
            });
        }

        checkBalanceLoading.value = true;

        await Promise.allSettled([
            onCheckBalance(),
            ordersGet(),
        ]);

        // show modal
        if (balancesRefs.value != null && orders.value != null) {
            show.value = true;
        }

        checkBalanceLoading.value = false;
    };

    const onCheckBalance = async () => {
        const prepare = await WalletsService.getBalances(props.botShortInfo.api_id);

        if (!prepare.data.status) {
            prepare.data.errors.forEach(({ msg }) => {
                notification['error']({
                    content: msg,
                    duration: 2500,
                    keepAliveOnHover: true,
                });
            });

            // reset balancesRefs
            balancesRefs.value = null;
        } else {
            balancesRefs.value = prepare.data.balances;
        }
    };

    const onAddInsuranceOrder = async () => {
        addInsuranceOrderLoading.value = true;

        try {
            const prepare = await StatsService.addInsuranceOrder({
                id: props.botShortInfo.id,
                amount: +model.amount,
                rate: +model.rate,
                market: model.market,
            });

            if (!prepare.data.status) {
                if (prepare.data?.errors) {
                    prepare.data.errors.forEach(({ msg }) => {
                        notification.error({
                            content: msg,
                            duration: 2500,
                            keepAliveOnHover: true,
                        });
                    });
                }
            } else {
                prepare.postMessages.forEach(({ msg }) => {
                    notification.success({
                        content: msg,
                        duration: 2500,
                        keepAliveOnHover: true,
                    });
                });

                // close modal
                show.value = false;
            }
        } catch {
            notification.error({
                content: t('errorMessage'),
                duration: 2500,
                keepAliveOnHover: true,
            });
        };

        addInsuranceOrderLoading.value = false;
    };

    const ordersGet = async () => {
        try {
            const prepare = await StatsService.ordersGet({
                pager: ordersFilters.value,
            });

            if (prepare.data.status) {
                orders.value = prepare.data.records;
            } else {
                prepare.data.errors.forEach(({ msg }) => {
                    notification['error']({
                        content: msg,
                        duration: 2500,
                        keepAliveOnHover: true,
                    });
                });

                // reset orders
                orders.value = null;
            }
        } catch {
            gl.showNotification({
                type: 'error',
                msg: t('errorMessage'),
            });
        };
    };

    const setPrice = price => {
        if (!model.market) {
            model.rate = price;
        }
    };

    return {
        gl,
        r1,
        r2,
        coin,
        show,
        stats,
        model,
        takeprofit,
        priceToZero,
        symbolBalance,
        calculateShow,
        orderExecution,
        takeprofitShow,
        checkBalanceLoading,
        orderExecutionTypes,
        addInsuranceOrderLoading,
        t,
        setPrice,
        onCheckBalance,
        onInsuranceOrder,
        onAddInsuranceOrder,
        currency: icon => refs.currencies.find(el => el.id === icon),
        exchange: exchange => refs.exchanges.find(el => el.id === exchange),
    };
}